import {
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  PlusOutlined,
  UnderlineOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Modal, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import * as Draft from 'draft-js';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationEditorModel, NotificationFieldInfo } from './NotificationEditorModel';

import s from './NotificationEditor.module.scss';

type NotificationEditorProps = {
  model: NotificationEditorModel;
  fields: NotificationFieldInfo[];
  placeholder?: string;
};

const NotificationEditor: React.FC<NotificationEditorProps> = ({
  model,
  fields,
  placeholder,
}) => {
  const { t } = useTranslation();
  const editorRef = React.useRef<Draft.Editor | null>(null);
  const [linkModalVisible, setLinkModalVisible] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');

  const editorState = model.editorState;

  const handleKeyCommand = React.useCallback(
    (command: string, state: Draft.EditorState): Draft.DraftHandleValue => {
      const newState = Draft.RichUtils.handleKeyCommand(state, command);
      if (newState) {
        model.setEditorState(newState);
        return 'handled';
      }
      return 'not-handled';
    },
    [model]
  );

  const handleChange = React.useCallback(
    (newEditorState: Draft.EditorState) => {
      model.setEditorState(newEditorState);
    },
    [model]
  );

  const toggleInlineStyle = React.useCallback(
    (style: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      model.setEditorState(Draft.RichUtils.toggleInlineStyle(editorState, style));
    },
    [editorState, model]
  );

  const hasInlineStyle = React.useCallback(
    (style: string): boolean => {
      return editorState.getCurrentInlineStyle().has(style);
    },
    [editorState]
  );

  const handleLinkClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const selection = editorState.getSelection();
      if (!selection.isCollapsed()) {
        setLinkUrl('');
        setLinkModalVisible(true);
      }
    },
    [editorState]
  );

  const handleLinkConfirm = React.useCallback(() => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {
      url: linkUrl,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = Draft.EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });
    model.setEditorState(
      Draft.RichUtils.toggleLink(
        newEditorState,
        newEditorState.getSelection(),
        entityKey
      )
    );
    setLinkModalVisible(false);
    setLinkUrl('');
  }, [editorState, linkUrl, model]);

  const handleLinkCancel = React.useCallback(() => {
    setLinkModalVisible(false);
    setLinkUrl('');
  }, []);

  const focusEditor = React.useCallback(() => {
    editorRef.current?.focus();
  }, []);

  const handleAddField = React.useCallback(
    (field: NotificationFieldInfo) => {
      model.addFieldPlaceholder(field);
      // Возвращаем фокус в редактор
      setTimeout(() => {
        editorRef.current?.focus();
      }, 0);
    },
    [model]
  );

  // Создаём элементы меню для dropdown
  const fieldMenuItems: MenuProps['items'] = React.useMemo(() => {
    return fields.map((field) => ({
      key: field.id,
      label: field.name,
      onClick: () => handleAddField(field),
    }));
  }, [fields, handleAddField]);

  return (
    <div className={s.notificationEditor} onClick={(e) => e.stopPropagation()}>
      <div className={s.toolbar}>
        <Tooltip title={t('formList.notifications.formatting.bold')}>
          <Button
            type={hasInlineStyle('BOLD') ? 'primary' : 'default'}
            size="small"
            icon={<BoldOutlined />}
            onMouseDown={toggleInlineStyle('BOLD')}
          />
        </Tooltip>
        <Tooltip title={t('formList.notifications.formatting.italic')}>
          <Button
            type={hasInlineStyle('ITALIC') ? 'primary' : 'default'}
            size="small"
            icon={<ItalicOutlined />}
            onMouseDown={toggleInlineStyle('ITALIC')}
          />
        </Tooltip>
        <Tooltip title={t('formList.notifications.formatting.underline')}>
          <Button
            type={hasInlineStyle('UNDERLINE') ? 'primary' : 'default'}
            size="small"
            icon={<UnderlineOutlined />}
            onMouseDown={toggleInlineStyle('UNDERLINE')}
          />
        </Tooltip>
        <Tooltip title={t('formList.notifications.formatting.link')}>
          <Button
            size="small"
            icon={<LinkOutlined />}
            onMouseDown={handleLinkClick}
          />
        </Tooltip>

        <div className={s.toolbarDivider} />

        <Dropdown
          menu={{ items: fieldMenuItems }}
          trigger={['click']}
          placement="bottomLeft"
          overlayClassName={s.addFieldDropdown}
          disabled={fields.length === 0}
        >
          <Tooltip title={t('formList.notifications.addField', 'Добавить столбец')}>
            <Button size="small" icon={<PlusOutlined />}>
              {t('formList.notifications.addFieldButton', 'Столбец')}
            </Button>
          </Tooltip>
        </Dropdown>
      </div>

      <div className={s.editorContainer} onClick={focusEditor}>
        <Draft.Editor
          ref={editorRef}
          editorState={editorState}
          onChange={handleChange}
          handleKeyCommand={handleKeyCommand}
          placeholder={placeholder}
        />
      </div>

      <Modal
        title={t('formList.notifications.formatting.linkPrompt')}
        open={linkModalVisible}
        onOk={handleLinkConfirm}
        onCancel={handleLinkCancel}
        okText={t('formList.notifications.formatting.addLink')}
        cancelText={t('formList.notifications.formatting.cancel')}
      >
        <Input
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="https://"
          onPressEnter={handleLinkConfirm}
        />
      </Modal>
    </div>
  );
};

export default observer(NotificationEditor);
